import React, { useState } from 'react';
import {
  Box,
  Typography,
  Tooltip,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TableContainer,
  TableHead,
  TableBody,
  Button,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { InfoOutlined, KeyboardArrowDown, KeyboardArrowRight, KeyboardDoubleArrowRight, KeyboardDoubleArrowDown, Add, EditOutlined, DeleteOutline, DeleteSweep, MoreHoriz, FileUploadOutlined } from '@mui/icons-material';
import ProgramSettingsBiochemicalTable from '../ProgramDashboard/ProgramSettingsBiochemicalTable';
import { StyledPaper, StyledTable, StyledTableRow, StyledTableCell, StyledTableSortLabel } from '../../uiComponents/MuiTable/StyledTable';
import { parseAnalyteCodeString } from './cutoffUtils';
import AddConditionDialog from './AddConditionDialog';
import ModifyPrimaryAnalyteDialog from './ModifyPrimaryAnalyteDialog';
import ImportCutoffsDialog from './ImportCutoffsDialog';

const BiochemicalSettingsTab = ({ tabProps }) => {
  const {
    biochemicalDirty,
    biochemicalError,
    setBiochemicalError,
    ed3nConfig,
    tooltipProps,
    infoIconStyle,
    biochemicalInstrumentation,
    selectedBiochemicalValues,
    handleBiochemicalTableSelectionChange,
    cutoffSettingsSelection,
    handleCutoffSettingsSelectionChange,
    CUTOFF_AUTO_OPTION,
    CUTOFF_MANUAL_OPTION,
    cutoffTableHeadCells,
    cutoffTableOrder,
    cutoffTableOrderBy,
    handleCutoffTableRequestSort,
    cutoffLoading,
    sortedCutoffValues,
    sortedDefaultCutoffValues,
    expandedCutoffRows,
    handleToggleCutoffRow,
    setExpandedCutoffRows,
    getCutoffConditionDisplayName,
    getAnalyteDisplayName,
    CUTOFF_DIALOG_MODE_ADD,
    CUTOFF_DIALOG_MODE_MODIFY,
    openCutoffDialog,
    handleRequestDeleteCutoff,
    handleRequestDeleteAllCutoffs,
    hasManualCutoffs,
    cutoffDeleteSaving,
    stickyActionBarSx,
    renderDisabledInfoIcon,
    handleCancelBiochemical,
    biochemicalSaving,
    onSubmitBiochemical,
    handleOpenAddConditionDialog,
    handleCloseAddConditionDialog,
    handleAddConditionFromDialog,
    addConditionDialogOpen,
    availableConditionsForAdd,
    analyteLookupOptions,
    modifyPrimaryAnalyteDialogOpen,
    modifyPrimaryAnalyteTarget,
    modifyPrimaryAnalyteSaving,
    handleOpenModifyPrimaryAnalyteDialog,
    handleCloseModifyPrimaryAnalyteDialog,
    handleSaveModifyPrimaryAnalyte,
    importCutoffsDialogOpen,
    importCutoffsImporting,
    handleOpenImportCutoffsDialog,
    handleCloseImportCutoffsDialog,
    handleImportCutoffs,
  } = tabProps;

  // Static table head cells (no Actions column)
  const staticTableHeadCells = cutoffTableHeadCells.filter(cell => cell.id !== 'actions');

  // --- Temp-row warn dialog ---
  const [tempRowWarnOpen, setTempRowWarnOpen] = useState(false);

  const emptyTempRows = sortedCutoffValues.filter((c) => {
    if (!c.isTempRow) return false;
    const profile = c.conditionProfile || c.ConditionProfile;
    return !Array.isArray(profile) || profile.length === 0;
  });

  const handleApplyClick = () => {
    if (emptyTempRows.length > 0) {
      setTempRowWarnOpen(true);
    } else {
      onSubmitBiochemical();
    }
  };

  const handleConfirmApply = () => {
    setTempRowWarnOpen(false);
    onSubmitBiochemical();
  };

  // Collect all unique secondary analyte display names from all profile rows of a condition
  const getConditionSecondaryAnalytes = (profileRows) => {
    const allCodes = new Set();
    profileRows.forEach((cutoff) => {
      const analyteCode1List = parseAnalyteCodeString(cutoff?.analyteCode1);
      const analyteCode2List = parseAnalyteCodeString(cutoff?.analyteCode2);
      const codes = Array.isArray(cutoff.analyteCodes) && cutoff.analyteCodes.length
        ? cutoff.analyteCodes
        : [...analyteCode1List, ...analyteCode2List];
      codes.filter(code => !!code).forEach(code => allCodes.add(code));
    });
    return [...allCodes].map(code => getAnalyteDisplayName(code)).filter(name => !!name);
  };

  // Display multiple primary analytes (comma-separated codes)
  const getPrimaryAnalyteDisplay = (primaryAnalyte) => {
    if (!primaryAnalyte) return '';
    const codes = parseAnalyteCodeString(primaryAnalyte);
    return codes.map(code => getAnalyteDisplayName(code)).filter(name => !!name).join(', ');
  };

  // Helper: compute row key for default (static) table rows
  const getDefaultRowKey = (condition, index) =>
    condition.orphanetCode || condition.OrphanetCode || condition.name || condition.Name || `default-cutoff-row-${index}`;

  // Helper: compute row key for editable table rows
  const getEditableRowKey = (condition, index) =>
    condition.orphanetCode || condition.OrphanetCode || condition.name || condition.Name || `cutoff-row-${index}`;

  // Expandable rows with precomputed keys for default table
  const expandableDefaultRows = sortedDefaultCutoffValues
    .map((c, i) => ({ condition: c, rowKey: `default-${getDefaultRowKey(c, i)}` }))
    .filter(({ condition }) => {
      const profile = condition.conditionProfile || condition.ConditionProfile;
      return Array.isArray(profile) && profile.length > 0;
    });

  const allDefaultExpanded =
    expandableDefaultRows.length > 0 &&
    expandableDefaultRows.every(({ rowKey }) => !!expandedCutoffRows[rowKey]);

  const handleToggleAllDefault = () => {
    const newValue = !allDefaultExpanded;
    const updatedRows = { ...expandedCutoffRows };
    expandableDefaultRows.forEach(({ rowKey }) => { updatedRows[rowKey] = newValue; });
    setExpandedCutoffRows(updatedRows);
  };

  // Expandable rows with precomputed keys for editable table
  const expandableEditableRows = sortedCutoffValues
    .map((c, i) => ({ condition: c, rowKey: getEditableRowKey(c, i) }))
    .filter(({ condition }) => {
      const profile = condition.conditionProfile || condition.ConditionProfile;
      return Array.isArray(profile) && profile.length > 0;
    });

  const allEditableExpanded =
    expandableEditableRows.length > 0 &&
    expandableEditableRows.every(({ rowKey }) => !!expandedCutoffRows[rowKey]);

  const handleToggleAllEditable = () => {
    const newValue = !allEditableExpanded;
    const updatedRows = { ...expandedCutoffRows };
    expandableEditableRows.forEach(({ rowKey }) => { updatedRows[rowKey] = newValue; });
    setExpandedCutoffRows(updatedRows);
  };

  // Style for visually hidden text (screen-reader only)
  const visuallyHiddenStyle = {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  };

  // Renders the table header with a leading "expand all" icon column
  const renderCutoffColumnHeader = (headCells, allExpanded, hasExpandable, onToggleAll) => (
    <TableHead>
      <StyledTableRow>
        <StyledTableCell sx={{ px: '4px' }}>
          <Tooltip title={allExpanded ? 'Collapse all rows' : 'Expand all rows'} placement="top">
            <span>
              <IconButton
                size="small"
                onClick={onToggleAll}
                disabled={!hasExpandable}
                aria-label={allExpanded ? 'Collapse all rows' : 'Expand all rows'}
                sx={{ color: 'inherit' }}
              >
                {allExpanded
                  ? <KeyboardDoubleArrowDown fontSize="small" />
                  : <KeyboardDoubleArrowRight fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        </StyledTableCell>
        {headCells.map((headCell) => {
          const cellType = headCell.type || 'sortable';
          if (cellType === 'blank') {
            return (
              <StyledTableCell key={headCell.id}>
                <span style={visuallyHiddenStyle}>{headCell.hiddenHeader || headCell.label || ''}</span>
              </StyledTableCell>
            );
          } else if (cellType === 'noSort') {
            return (
              <StyledTableCell key={headCell.id}>
                {headCell.label}
              </StyledTableCell>
            );
          } else {
            return (
              <StyledTableCell
                key={headCell.id}
                sortDirection={cutoffTableOrderBy === headCell.id ? cutoffTableOrder : false}
              >
                <StyledTableSortLabel
                  active={cutoffTableOrderBy === headCell.id}
                  direction={cutoffTableOrderBy === headCell.id ? cutoffTableOrder : 'asc'}
                  onClick={(e) => handleCutoffTableRequestSort(e, headCell.id)}
                >
                  {headCell.label}
                  {cutoffTableOrderBy === headCell.id ? (
                    <span style={visuallyHiddenStyle}>
                      {cutoffTableOrder === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </span>
                  ) : null}
                </StyledTableSortLabel>
              </StyledTableCell>
            );
          }
        })}
      </StyledTableRow>
    </TableHead>
  );

  const renderStaticCutoffTable = () => (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <StyledPaper>
        <TableContainer>
          <StyledTable size="small" aria-label="Default analyte cutoff settings" style={{ tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '5%' }} />
              <col style={{ width: '35%' }} />
              <col style={{ width: '20%' }} />
              <col style={{ width: '20%' }} />
              <col style={{ width: '20%' }} />
            </colgroup>
            {renderCutoffColumnHeader(
              staticTableHeadCells,
              allDefaultExpanded,
              expandableDefaultRows.length > 0,
              handleToggleAllDefault,
            )}
            <TableBody>
              {cutoffLoading ? (
                <StyledTableRow>
                  <StyledTableCell colSpan={5} style={{ textAlign: 'center' }}>
                    <CircularProgress size={24} />
                  </StyledTableCell>
                </StyledTableRow>
              ) : sortedDefaultCutoffValues.length === 0 ? (
                <StyledTableRow>
                  <StyledTableCell colSpan={5} style={{ textAlign: 'center' }}>
                    No default analyte cutoff settings found
                  </StyledTableCell>
                </StyledTableRow>
              ) : (
                sortedDefaultCutoffValues.map((condition, index) => {
                  const rowKey = getDefaultRowKey(condition, index);
                  const profileRows = Array.isArray(condition.conditionProfile || condition.ConditionProfile) ? (condition.conditionProfile || condition.ConditionProfile) : [];
                  const isExpanded = !!expandedCutoffRows[`default-${rowKey}`];
                  const secondaryAnalytes = getConditionSecondaryAnalytes(profileRows).join(', ');

                  return (
                    <React.Fragment key={rowKey}>
                      <StyledTableRow>
                        <StyledTableCell sx={{ px: '4px' }}>
                          <IconButton
                            size="small"
                            onClick={() => handleToggleCutoffRow(`default-${rowKey}`)}
                            aria-label={isExpanded ? 'Collapse condition cutoffs' : 'Expand condition cutoffs'}
                          >
                            {isExpanded ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
                          </IconButton>
                        </StyledTableCell>
                        <StyledTableCell>
                          <Typography variant="body2">{getCutoffConditionDisplayName(condition)}</Typography>
                        </StyledTableCell>
                        <StyledTableCell>{getPrimaryAnalyteDisplay(condition.primaryAnalyte || condition.PrimaryAnalyte)}</StyledTableCell>
                        <StyledTableCell>{secondaryAnalytes}</StyledTableCell>
                        <StyledTableCell>{profileRows.length}</StyledTableCell>
                      </StyledTableRow>

                      {isExpanded && profileRows.map((cutoff, cutoffIndex) => {
                        return (
                          <StyledTableRow key={`${rowKey}-cutoff-${cutoffIndex}`}>
                            <StyledTableCell />
                            <StyledTableCell>
                              <Box sx={{ pl: 3 }}>
                                <Tooltip title={cutoff.flagMessage && cutoff.flagMessage.length > 100 ? cutoff.flagMessage : ''} placement="top" arrow>
                                  <Typography variant="body2" component="span">
                                    {cutoff.flagMessage && cutoff.flagMessage.length > 100
                                      ? cutoff.flagMessage.slice(0, 100)
                                      : cutoff.flagMessage}
                                  </Typography>
                                </Tooltip>
                                {cutoff.flagMessage && cutoff.flagMessage.length > 100 && (
                                  <Tooltip title={cutoff.flagMessage} placement="top" arrow>
                                    <span
                                      tabIndex={0}
                                      role="img"
                                      aria-label={`Full flag message: ${cutoff.flagMessage}`}
                                      style={{ display: 'inline', cursor: 'pointer' }}
                                    >
                                      <MoreHoriz style={{ fontSize: '1rem', color: 'var(--info-color)', verticalAlign: '-0.43em' }} />
                                    </span>
                                  </Tooltip>
                                )}
                              </Box>
                            </StyledTableCell>
                            <StyledTableCell />
                            <StyledTableCell />
                            <StyledTableCell />
                          </StyledTableRow>
                        );
                      })}
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </StyledTable>
        </TableContainer>
      </StyledPaper>
    </Box>
  );

  const renderEditableCutoffTable = () => (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <StyledPaper>
        <TableContainer>
          <StyledTable size="small" aria-label="Manual analyte cutoff settings" style={{ tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '5%' }} />
              <col style={{ width: '30%' }} />
              <col style={{ width: '20%' }} />
              <col style={{ width: '20%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '15%' }} />
            </colgroup>
            {renderCutoffColumnHeader(
              cutoffTableHeadCells,
              allEditableExpanded,
              expandableEditableRows.length > 0,
              handleToggleAllEditable,
            )}
            <TableBody>
              {cutoffLoading ? (
                <StyledTableRow>
                  <StyledTableCell colSpan={6} style={{ textAlign: 'center' }}>
                    <CircularProgress size={24} />
                  </StyledTableCell>
                </StyledTableRow>
              ) : sortedCutoffValues.length === 0 ? (
                <StyledTableRow>
                  <StyledTableCell colSpan={6} style={{ textAlign: 'center' }}>
                    No analyte cutoff settings found
                  </StyledTableCell>
                </StyledTableRow>
              ) : (
                sortedCutoffValues.map((condition, index) => {
                  const rowKey = getEditableRowKey(condition, index);
                  const profileRows = Array.isArray(condition.conditionProfile || condition.ConditionProfile) ? (condition.conditionProfile || condition.ConditionProfile) : [];
                  const hasProfileRows = profileRows.length > 0;
                  const isExpanded = !!expandedCutoffRows[rowKey];
                  const secondaryAnalytes = getConditionSecondaryAnalytes(profileRows).join(', ');
                  const isTemp = !!condition.isTempRow;

                  return (
                    <React.Fragment key={rowKey}>
                      <StyledTableRow style={isTemp ? { backgroundColor: 'var(--accent-quaternary-color)' } : undefined}>
                        <StyledTableCell sx={{ px: '4px' }}>
                          <IconButton
                            size="small"
                            onClick={() => handleToggleCutoffRow(rowKey)}
                            aria-label={isExpanded ? 'Collapse condition cutoffs' : 'Expand condition cutoffs'}
                            disabled={!hasProfileRows}
                            sx={!hasProfileRows ? { color: 'rgba(0, 0, 0, 0.26)' } : undefined}
                          >
                            {isExpanded ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
                          </IconButton>
                        </StyledTableCell>
                        <StyledTableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body2">{getCutoffConditionDisplayName(condition)}</Typography>
                            {isTemp && (
                              <Tooltip title="Not yet saved. Add a cutoff to save." {...tooltipProps}>
                                <span
                                  tabIndex={0}
                                  role="img"
                                  aria-label="Not yet saved. Add a cutoff to save."
                                  style={{ display: 'inline-flex', alignItems: 'center', marginLeft: 4 }}
                                >
                                  <InfoOutlined style={{ ...infoIconStyle, marginLeft: 0, marginRight: 0 }} />
                                </span>
                              </Tooltip>
                            )}
                          </Box>
                        </StyledTableCell>
                        <StyledTableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="body2">{getPrimaryAnalyteDisplay(condition.primaryAnalyte || condition.PrimaryAnalyte)}</Typography>
                            <Tooltip title="Modify Primary Analyte(s)" {...tooltipProps}>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenModifyPrimaryAnalyteDialog(condition)}
                                aria-label="Modify Primary Analyte(s)"
                                disabled={cutoffDeleteSaving || modifyPrimaryAnalyteSaving}
                                color="primary"
                                sx={{
                                  border: '1px solid',
                                  borderColor: cutoffDeleteSaving || modifyPrimaryAnalyteSaving ? 'rgba(0,0,0,0.12)' : 'primary.main',
                                  borderRadius: 1,
                                  p: '3px',
                                }}
                              >
                                <EditOutlined fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </StyledTableCell>
                        <StyledTableCell>{secondaryAnalytes}</StyledTableCell>
                        <StyledTableCell>{profileRows.length}</StyledTableCell>
                        <StyledTableCell>
                          <Tooltip title="Add Cutoff" {...tooltipProps}>
                            <span style={{ display: 'block' }}>
                              <Button
                                fullWidth
                                variant="outlined"
                                size="small"
                                onClick={() => openCutoffDialog(CUTOFF_DIALOG_MODE_ADD, condition)}
                                startIcon={<Add fontSize="small" />}
                                disabled={cutoffDeleteSaving}
                              >
                                Add
                              </Button>
                            </span>
                          </Tooltip>
                        </StyledTableCell>
                      </StyledTableRow>

                      {isExpanded && profileRows.map((cutoff, cutoffIndex) => {
                        const isPendingSave = !!cutoff.isPendingSave;
                        return (
                          <StyledTableRow
                            key={`${rowKey}-cutoff-${cutoffIndex}`}
                            style={isPendingSave ? { backgroundColor: 'var(--accent-quaternary-color)' } : undefined}
                          >
                            <StyledTableCell />
                            <StyledTableCell>
                              <Box sx={{ pl: 3, display: 'flex', alignItems: 'center' }}>
                                <Tooltip title={cutoff.flagMessage && cutoff.flagMessage.length > 100 ? cutoff.flagMessage : ''} placement="top" arrow>
                                  <Typography variant="body2" component="span">
                                    {cutoff.flagMessage && cutoff.flagMessage.length > 100
                                      ? cutoff.flagMessage.slice(0, 100)
                                      : cutoff.flagMessage}
                                  </Typography>
                                </Tooltip>
                                {cutoff.flagMessage && cutoff.flagMessage.length > 100 && (
                                  <Tooltip title={cutoff.flagMessage} placement="top" arrow>
                                    <span
                                      tabIndex={0}
                                      role="img"
                                      aria-label={`Full flag message: ${cutoff.flagMessage}`}
                                      style={{ display: 'inline', cursor: 'pointer' }}
                                    >
                                      <MoreHoriz style={{ fontSize: '1rem', color: 'var(--info-color)', verticalAlign: '-0.43em' }} />
                                    </span>
                                  </Tooltip>
                                )}
                                {isPendingSave && (
                                  <Tooltip title="Not yet saved. Click Apply to save." {...tooltipProps}>
                                    <span
                                      tabIndex={0}
                                      role="img"
                                      aria-label="Not yet saved. Click Apply to save."
                                      style={{ display: 'inline-flex', alignItems: 'center', marginLeft: 4 }}
                                    >
                                      <InfoOutlined style={{ ...infoIconStyle, marginLeft: 0, marginRight: 0 }} />
                                    </span>
                                  </Tooltip>
                                )}
                              </Box>
                            </StyledTableCell>
                            <StyledTableCell />
                            <StyledTableCell />
                            <StyledTableCell />
                            <StyledTableCell>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <Button
                                  fullWidth
                                  variant="outlined"
                                  size="small"
                                  onClick={() => openCutoffDialog(CUTOFF_DIALOG_MODE_MODIFY, condition, cutoffIndex, cutoff)}
                                  startIcon={<EditOutlined fontSize="small" />}
                                  disabled={cutoffDeleteSaving}
                                >
                                  Modify
                                </Button>
                                <Button
                                  fullWidth
                                  variant="outlined"
                                  size="small"
                                  onClick={() => handleRequestDeleteCutoff(condition, cutoffIndex)}
                                  startIcon={<DeleteOutline fontSize="small" />}
                                  disabled={cutoffDeleteSaving}
                                >
                                  Delete
                                </Button>
                              </Box>
                            </StyledTableCell>
                          </StyledTableRow>
                        );
                      })}
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </StyledTable>
        </TableContainer>
      </StyledPaper>
    </Box>
  );

  return (
  <Box className="psTabContent">
    <Typography variant="h6" component="h2" className="psTabTitle">Biochemical Module Settings</Typography>
    {biochemicalDirty && (
      <Typography variant="caption" sx={{ color: 'var(--accent-primary-color)', fontWeight: 500, display: 'block', textAlign: 'right' }}>
        * Unsaved changes
      </Typography>
    )}

    {biochemicalError && (
      <Alert severity="error" sx={{ mb: 1 }} onClose={() => setBiochemicalError('')}>
        {biochemicalError}
      </Alert>
    )}

    {ed3nConfig.disableBiochemical !== true ? (
      <Box sx={{ mt: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="body1" sx={{ fontWeight: 500, textAlign: 'left' }}>
            Default Biochemical Instrumentation Settings:
          </Typography>
          <Tooltip
            title="Set the default instrumentation, reagent kit, and lot number settings for each analyte category. These default settings are applied whenever uploading new biochemical data."
            {...tooltipProps}
          >
            <span tabIndex={0} role="button" aria-label="Information about default biochemical instrumentation settings">
              <InfoOutlined style={{ ...infoIconStyle, marginLeft: '6px' }} />
            </span>
          </Tooltip>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <ProgramSettingsBiochemicalTable
            data={biochemicalInstrumentation}
            selectedValues={selectedBiochemicalValues}
            handleTableSelectionChange={handleBiochemicalTableSelectionChange}
          />
        </Box>

        <Box className="psFormGrid" sx={{ mt: 3 }}>
          <Box className="psFormRow">
            <Box className="psFormLabel">
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Analyte Cutoff Settings:
                </Typography>
                <Tooltip
                  title="Set which cutoff values for ED3N to use when determining which patients to flag for conditions"
                  {...tooltipProps}
                >
                  <span tabIndex={0} role="button" aria-label="Information about analyte cutoff settings">
                    <InfoOutlined style={infoIconStyle} />
                  </span>
                </Tooltip>
              </Box>
            </Box>
            <Box className="psFormInput">
              <FormControl
                variant="outlined"
                size="small"
                sx={{
                  minWidth: 360,
                  '& label.Mui-focused': { color: 'var(--accent-primary-color)' },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'var(--accent-primary-color)',
                  },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiSelect-select': {
                    color: 'var(--accent-primary-color)',
                  },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiSelect-icon': {
                    color: 'var(--accent-primary-color)',
                  },
                }}
              >
                <InputLabel id="analyteCutoffSettings-label">Analyte Cutoff Settings</InputLabel>
                <Select
                  labelId="analyteCutoffSettings-label"
                  id="analyteCutoffSettings"
                  name="analyteCutoffSettings"
                  value={cutoffSettingsSelection}
                  label="Analyte Cutoff Settings"
                  onChange={handleCutoffSettingsSelectionChange}
                  MenuProps={{
                    disableScrollLock: true,
                    disablePortal: true,
                    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                    transformOrigin: { vertical: 'top', horizontal: 'left' },
                  }}
                >
                  <MenuItem value={CUTOFF_AUTO_OPTION}>{CUTOFF_AUTO_OPTION}</MenuItem>
                  <MenuItem value={CUTOFF_MANUAL_OPTION}>{CUTOFF_MANUAL_OPTION}</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          <Box className="psFormRow" sx={{ mb: 1 }}>
            <Box className="psFormLabel">
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Analyte Cutoffs:
                </Typography>
                <Tooltip
                  title={cutoffSettingsSelection === CUTOFF_AUTO_OPTION
                    ? 'These are the automatically calculated ED3N cutoff values'
                    : 'Set analyte cutoff values to use when Analyte Cutoff Settings are set to "Use Manually Specified Cutoffs"'}
                  {...tooltipProps}
                >
                  <span tabIndex={0} role="button" aria-label="Information about analyte cutoff values">
                    <InfoOutlined style={infoIconStyle} />
                  </span>
                </Tooltip>
                {cutoffSettingsSelection === CUTOFF_MANUAL_OPTION && hasManualCutoffs && (
                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    onClick={handleRequestDeleteAllCutoffs}
                    startIcon={<DeleteSweep fontSize="small" />}
                    sx={{ ml: 1 }}
                  >
                    Delete All Cutoffs
                  </Button>
                )}
              </Box>
            </Box>
            <Box className="psFormInput" sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: '8px' }}>
              {cutoffSettingsSelection === CUTOFF_MANUAL_OPTION && (
                <>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleOpenImportCutoffsDialog}
                    startIcon={<FileUploadOutlined fontSize="small" />}
                  >
                    Import Cutoffs
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleOpenAddConditionDialog}
                    startIcon={<Add fontSize="small" />}
                  >
                    Add Condition
                  </Button>
                </>
              )}
            </Box>
          </Box>
        </Box>

        {cutoffSettingsSelection === CUTOFF_AUTO_OPTION && renderStaticCutoffTable()}
        {cutoffSettingsSelection === CUTOFF_MANUAL_OPTION && renderEditableCutoffTable()}

        <Box sx={stickyActionBarSx}>
          {!biochemicalDirty && renderDisabledInfoIcon('No changes to apply')}
          <Button
            variant="outlined"
            onClick={handleCancelBiochemical}
            disabled={!biochemicalDirty || biochemicalSaving}
            sx={{ mr: 1 }}
          >
            Cancel
          </Button>
          <Button
            variant={!biochemicalDirty || biochemicalSaving ? 'outlined' : 'contained'}
            color="primary"
            onClick={handleApplyClick}
            disabled={!biochemicalDirty || biochemicalSaving}
            startIcon={biochemicalSaving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {biochemicalSaving ? 'Applying...' : 'Apply'}
          </Button>
        </Box>
      </Box>
    ) : (
      <Typography variant="body1" sx={{ color: 'text.secondary', mt: 2 }}>
        Biochemical module is currently disabled.
      </Typography>
    )}

    <AddConditionDialog
      open={addConditionDialogOpen}
      onClose={handleCloseAddConditionDialog}
      onAdd={handleAddConditionFromDialog}
      availableConditions={availableConditionsForAdd}
      getCutoffConditionDisplayName={getCutoffConditionDisplayName}
      analyteLookupOptions={analyteLookupOptions}
    />
    <ModifyPrimaryAnalyteDialog
      open={modifyPrimaryAnalyteDialogOpen}
      onClose={handleCloseModifyPrimaryAnalyteDialog}
      onSave={handleSaveModifyPrimaryAnalyte}
      condition={modifyPrimaryAnalyteTarget}
      analyteLookupOptions={analyteLookupOptions}
      getCutoffConditionDisplayName={getCutoffConditionDisplayName}
      saving={modifyPrimaryAnalyteSaving}
    />
    <ImportCutoffsDialog
      open={importCutoffsDialogOpen}
      onClose={handleCloseImportCutoffsDialog}
      onImport={handleImportCutoffs}
      importing={importCutoffsImporting}
    />

    <Dialog
      open={tempRowWarnOpen}
      onClose={() => setTempRowWarnOpen(false)}
      aria-labelledby="temp-row-warn-title"
    >
      <DialogTitle id="temp-row-warn-title" sx={{ textAlign: 'center' }}>Unsaved Condition{emptyTempRows.length > 1 ? 's' : ''} Will Be Discarded</DialogTitle>
      <DialogContent>
        <DialogContentText>
          The following condition{emptyTempRows.length > 1 ? 's have' : ' has'} no cutoffs added and will be
          discarded when you apply:
        </DialogContentText>
        <Box component="ul" sx={{ mt: 1, mb: 0, pl: 3 }}>
          {emptyTempRows.map((c, i) => (
            <li key={i}>
              <Typography variant="body2">{getCutoffConditionDisplayName(c)}</Typography>
            </li>
          ))}
        </Box>
        <DialogContentText sx={{ mt: 1.5 }}>
          To keep a condition, cancel and add at least one cutoff before applying.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setTempRowWarnOpen(false)}>Cancel</Button>
        <Button onClick={handleConfirmApply} variant="contained" color="primary">Apply Anyway</Button>
      </DialogActions>
    </Dialog>
  </Box>
  );
};

export default BiochemicalSettingsTab;
